import { FocusTrap } from "@iunctim/component-overlay/Resources/Public/Scripts/features/FocusTrap.js";
import {
    Debounce,
    DebounceWithCancel,
} from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/Debounce.js";
import { DeepRequired } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/DeepRequired.js";
import { Behaviour } from "@iunctim/utility-frontend/Resources/Public/Scripts/utilities/ResponsiveBehaviour.js";

/**
 * Helpers to define possible configuration
 * options for the abstract popup-menu
 */
export interface PopupOptions {
    hover?: boolean;
    debounce?: {
        enter?: number;
        leave?: number;
    };
}

type PopupOptionsWithFallbacks = DeepRequired<PopupOptions>;

/**
 * Abstract implementation of a popup-menu, that can
 * be extended by a concrete menu implementation
 */
export abstract class Popup {
    public ignoreHoverLeave: boolean = false;
    private _options: PopupOptionsWithFallbacks;
    protected levels: Level<Popup>[] = [];

    /**
     * Constructor of the Popup
     *
     * @param {PopupOptions} [options={}]
     */
    protected constructor(options: PopupOptions = {}) {
        this._options = {
            hover: options.hover ?? false,
            debounce: {
                enter: options.debounce?.enter ?? 75,
                leave: options.debounce?.leave ?? 25,
            },
        };
    }

    /**
     * Method to access the options
     *
     * @returns {PopupOptionsWithFallbacks}
     */
    public get options(): PopupOptionsWithFallbacks {
        return this._options;
    }

    /**
     * Selects the submenus and their handlers of an element
     *
     * @param {HTMLElement | null} scope
     * @param {Set<Level<Popup>> | null} [ancestors=null]
     * @returns {Level<Popup>[]}
     */
    protected createLevels(
        scope: HTMLElement | null,
        ancestors: Set<Level<Popup>> | null = null,
    ): Level<Popup>[] {
        if (scope && !(scope instanceof HTMLOListElement)) {
            scope = scope.querySelector("ol:first-child");
        }
        if (!scope) return [];

        const selector = `:scope > li > [aria-haspopup="true"]`;
        const clickTargets = scope.querySelectorAll<HTMLElement>(selector); // svg

        return Array.from(clickTargets)
            .flatMap((clickTarget) => {
                const hoverTarget = clickTarget.parentElement; // li
                if (hoverTarget) {
                    const id = clickTarget.getAttribute("aria-controls");
                    const menu = document.querySelector<HTMLElement>(
                        `[id="${id}"]`,
                    ); // ol
                    if (menu) {
                        const _ancestors = ancestors
                            ? new Set(ancestors)
                            : new Set<Level<Popup>>();
                        const level = new Level(
                            this,
                            menu,
                            clickTarget,
                            hoverTarget,
                            _ancestors,
                        );

                        _ancestors.add(level);
                        return [level, ...this.createLevels(menu, _ancestors)];
                    }
                }
            })
            .filter((level): level is Level<Popup> => !!level);
    }

    /**
     * Method to close all submenus
     * except for any referenced level
     *
     * @param {Level<Popup> | null} [except=null]
     * @returns {void}
     */
    public closeSubmenus(except: Level<Popup> | null = null): void {
        this.levels.forEach((level) => {
            if (!except || !except.ancestors.has(level)) {
                level.close();
            }
        });
    }
}

/**
 * Level implementation for the abstract popup-menu
 */
export class Level<Parent extends Popup> implements Behaviour {
    // Focus-Trap for the level
    private focusTrap: FocusTrap;

    // Debounced open and close methods
    private debouncedOpen: DebounceWithCancel<() => void>;
    private debouncedClose: DebounceWithCancel<() => void>;

    /**
     * Constructor of the Level
     *
     * @param {Parent} parent
     * @param {HTMLElement} menu
     * @param {HTMLElement} clickTarget
     * @param {HTMLElement} hoverTarget
     * @param {Set<Level<Parent>>} ancestors
     */
    public constructor(
        private parent: Parent,
        private menu: HTMLElement,
        private clickTarget: HTMLElement,
        private hoverTarget: HTMLElement,
        public ancestors: Set<Level<Parent>>,
    ) {
        // Instantiate the focus-trap
        this.focusTrap = new FocusTrap(menu);

        // Define debounced open and close methods
        this.debouncedOpen = Debounce.cancellable(
            () => this.open(),
            this.parent.options.debounce.enter,
        );
        this.debouncedClose = Debounce.cancellable(
            () => this.close(),
            this.parent.options.debounce.leave,
        );
    }

    /**
     * Method to apply the functionality
     *
     * @returns {void}
     */
    public apply(): void {
        // Allow control via hover
        // for proper accessibility
        if (this.parent.options.hover) {
            this.hoverTarget.addEventListener(
                "mouseenter",
                this.handleMouseEnter,
            );
            this.hoverTarget.addEventListener(
                "mouseleave",
                this.handleMouseLeave,
            );
        }

        // Allow control via click
        // for proper accessibility
        this.clickTarget.addEventListener("click", this.handleClick);

        // Allow control via keydown
        // for proper accessibility
        this.hoverTarget.addEventListener("keydown", this.handleKeydown);
        this.clickTarget.addEventListener("keydown", this.handleKeydown);
        this.menu.addEventListener("keydown", this.handleKeydown);
    }

    /**
     * Method to destroy the functionality
     *
     * @returns {void}
     */
    public destroy(): void {
        // Remove control via hover
        if (this.parent.options.hover) {
            this.hoverTarget.removeEventListener(
                "mouseenter",
                this.handleMouseEnter,
            );
            this.hoverTarget.removeEventListener(
                "mouseleave",
                this.handleMouseLeave,
            );
        }

        // Remove control via click
        this.clickTarget.removeEventListener("click", this.handleClick);

        // Remove control via keydown
        this.hoverTarget.removeEventListener("keydown", this.handleKeydown);
        this.clickTarget.removeEventListener("keydown", this.handleKeydown);
        this.menu.removeEventListener("keydown", this.handleKeydown);

        // Cancel all debounded actions
        this.debouncedOpen.cancel();
        this.debouncedClose.cancel();

        // Set aria-expanded to false
        this.clickTarget.setAttribute("aria-expanded", "false");

        // Set the hidden attribute to true
        this.menu.hidden = true;
    }

    /**
     * Method to open the level
     *
     * @param {boolean} [useFocusTrap=false]
     * @returns {void}
     */
    private open(useFocusTrap: boolean = false): void {
        if (this.menu.hidden) {
            this.parent.closeSubmenus(this);
            this.clickTarget.setAttribute("aria-expanded", "true");
            this.menu.hidden = false;

            if (useFocusTrap) {
                this.focusTrap.enable(this.clickTarget);
            } else {
                this.focusTrap.setInitial(this.clickTarget);
            }
        }
    }

    /**
     * Method to close the level
     *
     * @param {boolean} [refocus=false]
     * @returns {void}
     */
    public close(refocus: boolean = false): void {
        if (!this.menu.hidden) {
            this.menu.hidden = true;
            this.clickTarget.setAttribute("aria-expanded", "false");

            this.focusTrap.disable(refocus);
        }
    }

    /**
     * Method to toggle the level
     *
     * @returns {void}
     */
    private toggle(): void {
        if (this.menu.hidden) {
            this.open();
        } else {
            this.close();
        }
    }

    /**
     * Handles the control via mouseenter-event
     *
     * @returns {void}
     */
    private handleMouseEnter = () => {
        this.debouncedClose.cancel();
        this.debouncedOpen();
    };

    /**
     * Handles the control via mouseleave-event
     *
     * @returns {void}
     */
    private handleMouseLeave = () => {
        if (!this.parent.ignoreHoverLeave) {
            this.debouncedOpen.cancel();
            this.debouncedClose();
        }
    };

    /**
     * Handles the control via click-event
     *
     * @param {MouseEvent} event
     * @returns {void}
     */
    private handleClick = (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();
        this.toggle();
    };

    /**
     * Handles the control via keydown-event
     *
     * @param {KeyboardEvent} event
     * @returns {void}
     */
    private handleKeydown = (event: KeyboardEvent) => {
        switch (event.key) {
            case "ArrowRight":
            case "ArrowDown":
                if (
                    event.target === this.clickTarget ||
                    event.currentTarget === this.hoverTarget
                ) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.open(true);
                }
                break;
            case "Enter":
                if (event.target === this.clickTarget) {
                    event.preventDefault();
                    event.stopPropagation();
                    this.open(true);
                }
                break;
            case "ArrowLeft":
            case "ArrowUp":
            case "Escape":
                if (this.menu.contains(event.target as Node)) {
                    event.preventDefault();
                    event.stopPropagation();

                    this.parent.ignoreHoverLeave = true;
                    this.close(true);

                    setTimeout(
                        () => (this.parent.ignoreHoverLeave = false),
                        this.parent.options.debounce.leave + 50,
                    );
                }
                break;
        }
    };
}
